'use client';

import Link from 'next/link';
import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle, ChevronRight } from 'lucide-react';
import { api } from '@/shared/services/api';
import { useCompany } from '@/features/settings/hooks/use-settings';
import { usePermissions } from '@/features/auth/hooks/use-permissions';

/**
 * L57 — Banner Dashboard pentru reminder Formular 082 ANAF.
 *
 * Vizibil DOAR pentru tenants cu legalForm IN ('PFA', 'II', 'IF') și
 * status f082 == 'pending'. Auto-hide când utilizatorul marchează „depus"
 * sau „neaplicabil" — fie din /setari?tab=f082, fie DIRECT de pe banner
 * (acțiunile inline de mai jos; cerere owner 2026-07-10: cardul rămânea
 * roșu pentru totdeauna fără o cale de închidere vizibilă pe dashboard).
 * Aplicația NU poate verifica automat Registrul RO e-Factura — statusul
 * e declarația utilizatorului, același endpoint ca tab-ul din Setări.
 */

const F082_DEADLINE = new Date('2026-05-26T23:59:59');
const PF_LEGAL_FORMS = ['PFA', 'II', 'IF'];

export function F082DeadlineBanner() {
  const { can } = usePermissions();
  const { data: company } = useCompany();
  const qc = useQueryClient();
  const [updating, setUpdating] = useState(false);

  // Aceeași mutație ca în /setari?tab=f082 (F082Tab.updateStatus) — banner-ul
  // dispare prin invalidarea query-ului 'company' (visible recalculat).
  const markStatus = async (status: 'submitted' | 'not_applicable') => {
    setUpdating(true);
    try {
      const submittedAt = status === 'submitted' ? new Date().toISOString() : undefined;
      await api.post('/tenants/current/f082-status', { status, submittedAt });
      qc.invalidateQueries({ queryKey: ['company'] });
      toast.success(status === 'submitted' ? 'Status marcat: depus la ANAF' : 'Status marcat: neaplicabil');
    } catch (err: any) {
      toast.error('Eroare la actualizare: ' + (err?.message || 'încearcă din nou'));
    } finally {
      setUpdating(false);
    }
  };

  const visible = useMemo(() => {
    if (!company) return false;
    const isPF = PF_LEGAL_FORMS.includes((company as any).legalForm || '');
    if (!isPF) return false;
    const settings = (company as any).settings || {};
    const status = settings.f082?.status;
    // Default 'pending' pentru PF când nu există state explicit
    return status !== 'submitted' && status !== 'not_applicable';
  }, [company]);

  const daysLeft = useMemo(() => {
    return Math.ceil((F082_DEADLINE.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  }, []);

  if (!visible) return null;

  const isPastDeadline = daysLeft < 0;
  const isUrgent = daysLeft >= 0 && daysLeft <= 7;
  const tone = isPastDeadline || isUrgent ? 'red' : 'amber';

  return (
    <div
      className={
        'rounded-xl border p-4 flex items-start gap-3 ' +
        (tone === 'red'
          ? 'border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-950/30'
          : 'border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/30')
      }
    >
      <AlertTriangle
        className={
          'h-5 w-5 mt-0.5 flex-shrink-0 ' +
          (tone === 'red' ? 'text-red-500' : 'text-amber-500')
        }
      />
      <div className="flex-1 min-w-0">
        <p
          className={
            'text-sm font-semibold ' +
            (tone === 'red' ? 'text-red-800 dark:text-red-300' : 'text-amber-800 dark:text-amber-300')
          }
        >
          {isPastDeadline
            ? 'Formular 082 ANAF — deadline depășit (26 mai 2026)'
            : daysLeft === 0
              ? 'Formular 082 ANAF — astăzi este ultima zi'
              : `Formular 082 ANAF — ${daysLeft} ${daysLeft === 1 ? 'zi' : 'zile'} până la deadline (26 mai 2026)`}
        </p>
        <p
          className={
            'text-sm mt-0.5 ' +
            (tone === 'red' ? 'text-red-700 dark:text-red-400' : 'text-amber-700 dark:text-amber-400')
          }
        >
          PFA-urile, II-urile și IF-urile cu activitate economică continuă trebuie înregistrate
          în Registrul RO e-Factura (Ordin ANAF 378/2026). Depune Formularul 082 prin SPV pentru
          a evita amenzile.
        </p>
        {/* Închidere direct de pe card — aceleași declarații ca în tab-ul din
            Setări; fără ele, cardul rămânea roșu permanent până găseai tab-ul. */}
        {/* declararea stării (POST /tenants/current/f082-status) cere `settings.company` */}
        {can('settings.company') && <div className="mt-2 flex items-center gap-3">
          <button
            type="button"
            disabled={updating}
            onClick={() => void markStatus('submitted')}
            className={
              'text-xs font-semibold underline underline-offset-2 disabled:opacity-50 ' +
              (tone === 'red' ? 'text-red-700 hover:text-red-900 dark:text-red-300' : 'text-amber-700 hover:text-amber-900 dark:text-amber-300')
            }
          >
            Am depus deja formularul
          </button>
          <button
            type="button"
            disabled={updating}
            onClick={() => void markStatus('not_applicable')}
            className={
              'text-xs underline underline-offset-2 disabled:opacity-50 ' +
              (tone === 'red' ? 'text-red-600/80 hover:text-red-800 dark:text-red-400' : 'text-amber-600/80 hover:text-amber-800 dark:text-amber-400')
            }
          >
            Nu mi se aplică
          </button>
        </div>}
      </div>
      <Link
        href="/setari?tab=f082"
        className={
          'inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors flex-shrink-0 ' +
          (tone === 'red'
            ? 'bg-red-600 text-white hover:bg-red-700'
            : 'bg-amber-600 text-white hover:bg-amber-700')
        }
      >
        Vezi ghidul
        <ChevronRight className="h-3 w-3" />
      </Link>
    </div>
  );
}
